import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, Button, PlusIcon, Spinner } from '@razorpay/blade/components';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { useModal } from 'components/ui/Modals';
import { RunPayrollData } from 'components/RunPayroll/utils/types';
import type { PreviewEntryInput } from 'api/gql-utils/overtime/types';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { OvertimeTable } from './OvertimeTable';
import { useOvertimeQueries } from './hooks/useOvertimeQueries';
import { useOvertimeMutations } from './hooks/useOvertimeMutations';
import { useAttendanceDefaults } from './hooks/useAttendanceDefaults';
import type { LocalEntry, OvertimeRef } from './types';
import { apiDataToLocalOTState, applyUbitechFormulaCards } from './utils';
import { routePaths } from 'components/Routes/data';
import { uniqueId } from 'utils/Numbers';
import { BulkUploadTypes } from 'constants/bulkUpload';

type OvertimeProps = {
  payrollEntry: RunPayrollData;
  payrollMonth: string;
};

export const Overtime = forwardRef<OvertimeRef, OvertimeProps>(
  ({ payrollEntry, payrollMonth: rawPayrollMonth }, ref) => {
    const [seeded, setSeeded] = useState(false);
    const [calculatingEntryId, setCalculatingEntryId] = useState<string | null>(null);
    const [entries, setEntries] = useState<LocalEntry[]>([]);
    const history = useHistory();

    const { closeModal } = useModal();

    const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
    const isUbitechOrg = !!currentOrganization?.isUbitechOrg;

    const userId = String(payrollEntry.peopleId);
    const payrollMonth = rawPayrollMonth.substring(0, 7);

    const { previewAmount } = useOvertimeMutations();

    const {
      baseDays,
      baseHours,
      isLoading: isLoadingBaseDays,
    } = useAttendanceDefaults(rawPayrollMonth);

    const {
      formulaCards: allFormulaCards,
      typeMappings,
      entries: apiEntries,
      isLoading,
      isError,
      refetch,
    } = useOvertimeQueries({
      payrollMonth,
      userId,
      enabled: !isLoadingBaseDays,
      baseDays: baseDays || undefined,
      onSuccess: (entries) => {
        setEntries(entries.map(apiDataToLocalOTState));
        setSeeded(true);
      },
    });

    const formulaCards = useMemo(
      () =>
        isUbitechOrg ? applyUbitechFormulaCards(allFormulaCards, typeMappings) : allFormulaCards,
      [isUbitechOrg, allFormulaCards, typeMappings],
    );

    useEffect(() => {
      if (!seeded && apiEntries.length > 0) {
        setEntries(apiEntries.map(apiDataToLocalOTState));
        setSeeded(true);
      } else if (!seeded && !isLoading && apiEntries.length === 0) {
        setSeeded(true);
      }
    }, [apiEntries, isLoading, seeded]);

    const addEntry = () => {
      setEntries((prev) => [
        ...prev,
        {
          id: uniqueId(),
          isNew: true,
          otFormulaCardId: null,
          totalOtHours: '',
          totalOtDays: '',
          workingHoursPerDay: baseHours,
          source: 'manual',
          calculatedAmount: null,
          isManuallyEdited: false,
          overrideAmount: null,
        },
      ]);
    };

    const updateEntry = (id: string, patch: Partial<LocalEntry>) => {
      setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
    };

    const removeEntry = (id: string) => {
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
    };

    const handleCalculate = (entryId: string) => {
      const entry = entries.find((e) => e.id === entryId);
      if (!entry || !entry.otFormulaCardId) return;

      const card = formulaCards.find((c) => c.id === entry.otFormulaCardId);
      if (!card) return;

      setCalculatingEntryId(entryId);

      const previewInput: PreviewEntryInput = {
        formulaCardId: entry.otFormulaCardId,
        formulaCardName: card.name,
        formulaTemplate: card.formulaTemplate,
        otHours: entry.totalOtHours || '0',
        otDays: entry.totalOtDays || '0',
        workingHoursPerDay: entry.workingHoursPerDay || baseHours,
        baseDays,
      };

      previewAmount.mutate(
        {
          userId: payrollEntry.peopleId,
          payrollMonth,
          entries: [previewInput],
        },
        {
          onSuccess: (response) => {
            const results = response.overtimeGetPreview.data.entries;
            const match = results.find((r) => r.formulaCardId === entry.otFormulaCardId);
            if (match) {
              updateEntry(entryId, { calculatedAmount: (match.computedAmount ?? 0) / 100 });
            }
            setCalculatingEntryId(null);
          },
          onError: () => {
            setCalculatingEntryId(null);
          },
        },
      );
    };

    const onBulkUpdate = () => {
      history.push({
        pathname: routePaths.bulkUploads.initiate,
        search: `type=${BulkUploadTypes.TYPE_BULK_OVERTIME_ADDITION}&payrollMonth=${rawPayrollMonth}`,
      });
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        let valid = true;
        setEntries((prev) =>
          prev.map((entry) => {
            const missingFormula = !entry.otFormulaCardId;
            if (missingFormula) {
              valid = false;
              return { ...entry, hasError: true };
            }
            const card = formulaCards.find((c) => c.id === entry.otFormulaCardId);
            const isDayUnit = card?.rateUnit === 'per_day';
            const primaryValue = isDayUnit ? entry.totalOtDays : entry.totalOtHours;
            const missingDailyAllowance =
              card?.dailyAllowance != null &&
              card.dailyAllowance > 0 &&
              !(parseFloat(entry.totalOtDays) > 0);
            const missingInput = !(parseFloat(primaryValue) > 0) || missingDailyAllowance;

            if (missingInput) {
              valid = false;
              return { ...entry, hasError: true };
            }
            return { ...entry, hasError: false };
          }),
        );
        return valid;
      },
      getEntries: () => entries,
      getBaseDays: () => baseDays,
    }));

    const isOTConfigured = formulaCards.length > 0;

    if (isLoading) {
      return (
        <ResetLocalOverridingStyles>
          <Box display="flex" justifyContent="center" padding="spacing.6">
            <Spinner accessibilityLabel="Loading overtime data" />
          </Box>
        </ResetLocalOverridingStyles>
      );
    }

    if (isError) {
      return (
        <ResetLocalOverridingStyles>
          <Alert
            color="negative"
            title="Failed to load overtime data"
            description="Something went wrong while loading overtime entries."
            isDismissible={false}
            actions={{
              primary: {
                text: 'Retry',
                onClick: refetch,
              },
            }}
          />
        </ResetLocalOverridingStyles>
      );
    }

    if (!isOTConfigured) {
      return (
        <ResetLocalOverridingStyles>
          <Alert
            color="information"
            title="Set up overtime to start tracking"
            description="Automate overtime calculations with formula-based rate cards."
            isDismissible={false}
            actions={{
              primary: {
                text: 'Set up Overtime Formulas →',
                onClick: () => {
                  closeModal();
                  history.push(routePaths.orgSettings.timeAndAttendance.payRules);
                },
              },
            }}
          />
        </ResetLocalOverridingStyles>
      );
    }

    if (entries.length === 0) {
      return (
        <ResetLocalOverridingStyles key="empty-overtime-list">
          <EmptyListView
            size="small"
            title=""
            description="No Overtime Added"
            paddingTop="spacing.4"
            paddingBottom="spacing.4">
            <Box display="flex" justifyContent="center" alignItems="center" gap="spacing.4">
              <Button size="xsmall" icon={PlusIcon} iconPosition="left" onClick={addEntry}>
                Add new overtime entry
              </Button>
              <Button size="xsmall" variant="secondary" onClick={onBulkUpdate}>
                Bulk update overtime
              </Button>
            </Box>
          </EmptyListView>
        </ResetLocalOverridingStyles>
      );
    }

    return (
      <ResetLocalOverridingStyles>
        <OvertimeTable
          entries={entries}
          formulas={formulaCards}
          onAdd={addEntry}
          onUpdate={updateEntry}
          onRemove={removeEntry}
          onCalculate={handleCalculate}
          calculatingEntryId={calculatingEntryId}
        />
      </ResetLocalOverridingStyles>
    );
  },
);
